import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import UnifiedConfirmDialog from '../components/UnifiedConfirmDialog';

interface Reservation {
  id: number;
  service: string;
  date: string;
  time: string;
  status: string;
}

const MyReservations = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [confirmConfig, setConfirmConfig] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => { } });

  const fetchReservations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/reservations', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReservations(res.data);
    } catch (err) {
      console.error('❌ 予約取得失敗:', err);
    }
  };

  const handleCancel = async (id: number) => {
    setConfirmConfig({
      open: true,
      title: t('common.confirm') || '確認',
      message: t('mypage.cancel_confirm', '本当にこの予約をキャンセルしますか？'),
      onConfirm: async () => {
        setConfirmConfig(prev => ({ ...prev, open: false }));
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`/api/reservations/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          alert(t('mypage.cancel_success', '予約をキャンセルしました'));
          fetchReservations(); // 重新加载
        } catch (err) {
          alert(t('mypage.cancel_failed', 'キャンセルに失敗しました'));
          console.error(err);
        }
      }
    });
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 px-6 font-sans">
      <Helmet>
        <title>{t('navbar.mypage')} - Melodis Clinic</title>
      </Helmet>
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4 text-center">
          {t('mypage.reservations', '診療一覧')}
        </h2>

        {reservations.length === 0 ? (
          <p className="text-center text-gray-600">{t('mypage.no_reservation', 'ご予約はありません')}</p>
        ) : (
          <ul className="space-y-4">
            {reservations.map((r) => (
              <li key={r.id} className="border p-4 rounded-lg flex justify-between items-center">
                <div>
                  <p className="text-lg font-semibold">{r.service}</p>
                  <p className="text-gray-600">{r.date} {r.time}</p>
                  <p className="text-sm text-gray-500">{t(`status.${r.status}`, r.status)}</p>
                </div>
                <button
                  className="text-sm bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600"
                  onClick={() => handleCancel(r.id)}
                >
                  {t('mypage.cancel', 'キャンセル')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <UnifiedConfirmDialog
        open={confirmConfig.open}
        onClose={() => setConfirmConfig(prev => ({ ...prev, open: false }))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        type="danger"
      />
    </div>
  );
};

export default MyReservations;
